import React, { useState } from "react";
import {
  Box,
  Button,
  Stack,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import InputText from "../../components/InputText";
import { useFormik } from "formik";
import Selector from "../../components/Selector";
import { generateClient } from "aws-amplify/api";
import { createPaymentDetails } from "../../graphql/mutations";
import { LoadingButton } from "@mui/lab";
import * as yup from "yup";
import { CircleCheckBig } from "lucide-react";
import { useNavigate } from "react-router-dom";

const SuccessModal = ({ open, onClose }) => {
  const navigate = useNavigate();
  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="success-dialog-title"
      aria-describedby="success-dialog-description"
      fullWidth
      maxWidth={"sm"}
      sx={{
        "& .MuiDialog-paper": {
          padding: 4,
        },
      }}
    >
      <DialogTitle sx={{ textAlign: "center" }} id="success-dialog-title">
        <img src="/assets/icons/success-check.svg" width={"80px"} />
      </DialogTitle>
      <DialogContent>
        <DialogContentText
          color={"primary.dark"}
          id="success-dialog-description"
          sx={{ textAlign: "center", fontSize: "25px", fontWeight: 500 }}
        >
          Payment details added successfully!
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ textAlign: "center", justifyContent: "center" }}>
        <Button
          variant="contained"
          color="primary"
          autoFocus
          sx={{ textTransform: "capitalize" }}
          onClick={() => {
            navigate("/payments");
          }}
        >
          Okay
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const validationSchema = yup.object().shape({
  accountName: yup
    .string()
    .required("Account name is required") // Ensure the field is not empty
    .min(2, "Account name must be at least 2 characters") // Minimum length
    .max(50, "Account name cannot exceed 50 characters"), // Maximum length

  companyName: yup
    .string()
    .required("Company name is required") // Ensure the field is not empty
    .min(2, "Company name must be at least 2 characters") // Minimum length
    .max(50, "Company name cannot exceed 50 characters"), // Maximum length

  IBANNumber: yup
    .string()
    .required("IBAN number is required") // Ensure the field is not empty
    .matches(
      /^[A-Z0-9]{15,34}$/,
      "IBAN number must be a valid format" // Regular expression to validate IBAN format (length between 15 and 34 alphanumeric characters)
    ),

  swiftCode: yup
    .string()
    .required("SWIFT code is required") // Ensure the field is not empty
    .matches(
      /^[A-Z0-9]{8,11}$/,
      "SWIFT code must be a valid format" // Regular expression to validate SWIFT/BIC code (length between 8 and 11 alphanumeric characters)
    ),

  bankName: yup
    .string()
    .required("Bank name is required") // Ensure the field is not empty
    .min(2, "Bank name must be at least 2 characters") // Minimum length
    .max(50, "Bank name cannot exceed 50 characters"), // Maximum length

  currency: yup
    .string()
    .required("Currency is required") // Ensure the field is not empty
    .oneOf(
      ["USD", "EUR", "GBP", "PKR", "INR"], // Example of valid currencies (add more if needed)
      "Currency must be one of USD, EUR, GBP, PKR, INR"
    ),

  contractValue: yup
    .number()
    .typeError("Contract value must be a number") // Ensure the field is a number
    .positive("Contract value must be a positive number") // Ensure the field is a positive number
    .required("Contract value is required"), // Ensure the field is not empty
});
const AddPaymentDetails = () => {
  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const handleCloseSuccessModal = () => {
    setOpenSuccessModal(false);
  };
  const client = generateClient();
  const [isLoading, setLoading] = useState(null);
  const formik = useFormik({
    initialValues: {
      accountName: "",
      companyName: "",
      IBANNumber: "",
      swiftCode: "",
      bankName: "",
      currency: "",
      contractValue: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      try {
        setLoading(true);
        const response = await client.graphql({
          query: createPaymentDetails,
          variables: {
            input: values,
          },
        });
        if (response.data.createPaymentDetails) {
          setOpenSuccessModal(true);
        }
      } catch (error) {
        console.log(error.message);
      } finally {
        setLoading(false);
      }
    },
  });
  return (
    <div>
      <Typography variant="h4" fontWeight={500}>
        Payment Details
      </Typography>
      <Box
        sx={{
          border: "1px solid lightgrey",
          borderRadius: "10px",
          padding: "10px",
        }}
        my={2}
      >
        <Typography variant="h6" fontWeight={400}>
          Enter Payment Details
        </Typography>
        <form onSubmit={formik.handleSubmit} action="">
          <Box display={"flex"} gap={4}>
            <Box width={"100%"}>
              <InputText
                label={"Account Holder Name"}
                formik={formik}
                name={"accountName"}
                placeholder={"Enter name here"}
                value={formik.values.accountName}
                id={"accountName"}
                type={"text"}
              />
              <InputText
                label={"IBAN Number"}
                formik={formik}
                name={"IBANNumber"}
                placeholder={"Enter  IBAN number"}
                value={formik.values.IBANNumber}
                id={"IBANNumber"}
                type={"text"}
              />
              <InputText
                label={"Bank Name"}
                formik={formik}
                name={"bankName"}
                placeholder={"Enter bank name"}
                value={formik.values.bankName}
                id={"bankName"}
                type={"text"}
              />
              <InputText
                label={"Contract Value"}
                formik={formik}
                name={"contractValue"}
                placeholder={"Enter contract value"}
                value={formik.values.contractValue}
                id={"contractValue"}
                type={"number"}
              />
            </Box>
            <Stack flexDirection={"column"} width={"100%"}>
              <InputText
                label={"Company Name"}
                formik={formik}
                name={"companyName"}
                placeholder={"Enter company name here"}
                value={formik.values.companyName}
                id={"companyName"}
                type={"text"}
              />
              <InputText
                label={"SWIFT / BIC Code"}
                formik={formik}
                name={"swiftCode"}
                placeholder={"Enter SWIFT / BIC Code here. "}
                value={formik.values.swiftCode}
                id={"swiftCode"}
                type={"text"}
              />
              <Selector
                label={"Currency"}
                name={"currency"}
                formik={formik}
                options={[
                  { label: "USD", value: "USD" },
                  { label: "EUR", value: "EUR" },
                  { label: "PKR", value: "PKR" },
                  { label: "GBP", value: "GBP" },
                ]}
                noneValue={"Select Currency"}
              />
              <LoadingButton
                loading={isLoading}
                type="submit"
                sx={{
                  marginTop: "auto",
                  color: "secondary.main",
                  backgroundColor: "primary.darker",
                  width: "150px",
                  alignSelf: "end",
                  textTransform: "capitalize",
                }}
                variant="contained"
              >
                Submit
              </LoadingButton>
            </Stack>
          </Box>
        </form>
      </Box>
      <SuccessModal open={openSuccessModal} onClose={handleCloseSuccessModal} />
    </div>
  );
};

export default AddPaymentDetails;
